import { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { Button, Checkbox, Form, Select } from 'semantic-ui-react';
import { useStore } from '../../../app/stores/store';
import Card from './card';
import Task from './task';
import Workflow from './workflow';
import DropdownCommon from './dropdown';
import Chart from './chart';
import FormConf from './form';
import DataGrid from './dataGrid';
import DataGridWithForm from './dataGridWithForm';
import { iconDetails, typeOptions } from './fixture';
import { defaultDashboardConfState } from '../../../app/stores/dashboardConfiguration';
import { IFormDetails } from '../../../app/stores/dashboardConfiguration/types';
import './styles.css';
import {
  static_items,
  static_items_id,
  static_workLevel,
  static_worklevel_id,
} from './constants';

function Settings({
  indexOfLevel,
  levelId,
  defaultType,
  defaultFlexBasis,
  defaultIcon,
  name,
}: {
  indexOfLevel: number;
  levelId: string;
  defaultType: string;
  defaultFlexBasis: string;
  name: string;
  defaultIcon?: string;
}) {
  // -------------------- Stores ------------------------ //
  const { DashboardConfigurationStore, sidebarStore, modalStore, PivotStore } =
    useStore();
  const {
    form,
    dataGrid,
    productionAndPlanning,
    workflow,
    pivotPlaceholder,
    chart,
    getDashboardSettingDetails,
    GETMasterMetaData,
  } = DashboardConfigurationStore || {};

  // -------------------- States ------------------------ //
  const [type, setType] = useState('');
  const [icon, setIcon] = useState('');
  const [flexBasis, setFlexBasis] = useState('0');
  const [isSubmitLoading, setIsSubmitLoading] = useState(false);

  useEffect(() => {
    setType(defaultType);
  }, [defaultType]);

  useEffect(() => {
    setIcon(defaultIcon || '');
  }, [defaultIcon]);

  useEffect(() => {
    setFlexBasis(defaultFlexBasis);
  }, [defaultFlexBasis]);

  useEffect(() => {
    switch (type) {
      case 'form':
        DashboardConfigurationStore.form = {
          ...DashboardConfigurationStore.form,
          data: getFormStaticUpdate(),
        };
        break;
      case 'dataGrid':
        DashboardConfigurationStore.dataGrid = {
          ...DashboardConfigurationStore.dataGrid,
          data: sidebarStore.masterFormList?.map((data) => ({
            key: data.masterId,
            value: data.title,
            text: data.title,
          })),
        };
        break;
    }
    // eslint-disable-next-line
  }, [type]);

  const getFormStaticUpdate = () => {
    const value = {
      masterId: '820682a2-4c97-4afc-b6a4-c3d155284da2',
      title: 'Items',
      icon: '',
    };
    const masterList = [...sidebarStore.masterFormList];
    const isDuplicate = masterList.some(
      (item) => item.masterId === value.masterId,
    );
    if (!isDuplicate) masterList.push(value);
    let data1: any[] = [];
    masterList?.forEach((data) => {
      const { masterId, title } = data;
      data1?.push({
        key: masterId,
        value: title,
        text: title,
      });
    });

    return data1;
  };

  useEffect(() => {
    (async () => {
      try {
        let result = await getDashboardSettingDetails(levelId);
        if (result !== undefined) {
          let type = Object.keys(result)?.[0] || '';
          let value = result[`${type}`] || {};
          let metaDataList: IFormDetails[] = [];
          if (value.masterId) {
            metaDataList = await GETMasterMetaData(value.masterId);
          }
          switch (type) {
            case 'form':
              DashboardConfigurationStore.form = {
                ...value,
                data: getFormStaticUpdate(),
                metaData: metaDataList?.filter(
                  (data) => data.isActive && !data.isDeleted,
                ),
              };
              break;
            case 'dataGrid':
              DashboardConfigurationStore.dataGrid = {
                ...value,
                data: sidebarStore.masterFormList?.map((data) => ({
                  key: data.masterId,
                  value: data.title,
                  text: data.title,
                })),
                metaData: metaDataList?.filter(
                  (data) => data.isActive && !data.isDeleted,
                ),
              };
              break;
            case 'card':
              DashboardConfigurationStore.card.data = value;
              DashboardConfigurationStore.card.categories = Object.entries(
                value,
              )?.flatMap(([key, localValue]: any) => {
                return !Object.keys(localValue || {})?.length || key === 'style'
                  ? []
                  : key;
              });
              break;
            case 'productionAndPlanning':
              DashboardConfigurationStore.productionAndPlanning = value;
              break;
            case 'pivotPlaceholder':
              DashboardConfigurationStore.pivotPlaceholder = value;
              break;
            case 'workflow':
              DashboardConfigurationStore.workflow.details = value;
              break;
            case 'chart':
              DashboardConfigurationStore.chart = value;
              break;
            case 'tableWithForm':
              DashboardConfigurationStore.tableWithForm = {
                ...DashboardConfigurationStore.tableWithForm,
                ...value,
              };
              break;
          }
        }
        DashboardConfigurationStore.isSettingLoading = false;
      } catch (e) {
        console.error(e);
      }
    })();
    // eslint-disable-next-line
  }, []);

  const componentRenderBasedOnType = () => {
    switch (type) {
      case 'card':
        return <Card />;
      case 'workflow':
        return <Workflow />;
      case 'task':
        return <Task />;
      case 'chart':
        return <Chart />;
      case 'form':
        return <FormConf />;
      case 'dataGrid':
        return <DataGrid />;
      case 'pivotPlaceholder':
        return (
          <DropdownCommon
            name="Pivot"
            placeholder="Select Pivot"
            options={PivotStore.saveAsPivotDataAll?.map((data) => ({
              key: data.id,
              value: data.tableName,
              text: data.tableName,
            }))}
            value={DashboardConfigurationStore.pivotPlaceholder.pivotName}
            onChange={(value) => {
              let filter =
                PivotStore.saveAsPivotDataAll?.filter(
                  (d) => d.tableName === value,
                )?.[0] || {};
              DashboardConfigurationStore.pivotPlaceholder.pivotName =
                filter.tableName || '';
              DashboardConfigurationStore.pivotPlaceholder.pivotId =
                filter.id || '';
            }}
          />
        );
      case 'productionAndPlanning':
        return (
          <div style={{ margin: '20px 0px 0px 0px' }}>
            <Checkbox
              checked={productionAndPlanning.needChart}
              label="Please select if you want charts"
              onChange={(_e: any, data: any) =>
                (DashboardConfigurationStore.productionAndPlanning.needChart =
                  data.checked)
              }
            />
          </div>
        );
      case 'tableWithForm':
        return <DataGridWithForm />;
      default:
        return <></>;
    }
  };

  const cancelHandler = () => {
    modalStore.modal.open = false;
    modalStore.modal.body = null;
  };

  const saveHandler = async () => {
    setIsSubmitLoading(true);

    let payload: any = {
      levelId,
      icon,
      type,
      flexBasis,
    };

    function clean(obj: any) {
      for (var propName in obj) {
        if (obj[propName] === null || obj[propName] === undefined) {
          delete obj[propName];
        }
      }
      return obj;
    }

    switch (type) {
      case 'card':
        payload = {
          ...payload,
          card: clean(DashboardConfigurationStore.card.data),
        };
        break;
      case 'form':
        payload = {
          ...payload,
          form: {
            masterName: form.masterName,
            masterId:
              form.data.filter((data) => data.value === form.masterName)?.[0]
                ?.key || '',
            masterElement: form.masterElement,
            automaticPdf: form.automaticPdf,
          },
        };
        break;
      case 'dataGrid':
        const tempMaster =
          dataGrid.data.filter(
            (data) => data.value === dataGrid.masterName,
          )?.[0]?.key || '';
        payload = {
          ...payload,
          dataGrid: {
            masterName: dataGrid.masterName,
            masterId:
              dataGrid.masterName === static_items
                ? static_items_id
                : dataGrid.masterName === static_workLevel
                  ? static_worklevel_id
                  : tempMaster,
            isMaster: dataGrid.isMaster,
            masterElement: dataGrid.masterElement,
            automaticPdf: dataGrid.automaticPdf,
            assetType: dataGrid.assetType,
            elementType: dataGrid.elementType,
          },
        };
        break;
      case 'productionAndPlanning':
        payload = {
          ...payload,
          productionAndPlanning,
        };
        break;
      case 'pivotPlaceholder':
        payload = {
          ...payload,
          pivot: pivotPlaceholder,
        };
        break;
      case 'workflow':
        payload = {
          ...payload,
          workflow: workflow.details,
        };
        break;
      case 'chart':
        payload = {
          ...payload,
          chart: chart,
        };
        break;
      case 'tableWithForm':
        let {
          formMasterId,
          formMasterName,
          formMasterElement,
          formFlexBasis,
          formLinkName,
          tableMasterId,
          tableMasterName,
          tableMasterElement,
          tableFlexBasis,
          tableLinkName,
        } = DashboardConfigurationStore.tableWithForm;
        payload = {
          ...payload,
          tableWithForm: {
            formMasterId,
            formMasterName,
            formMasterElement,
            formFlexBasis,
            formLinkName,
            tableMasterId,
            tableMasterName,
            tableMasterElement,
            tableFlexBasis,
            tableLinkName,
          },
        };
        break;
    }

    DashboardConfigurationStore.dashboardSetting(payload, indexOfLevel)
      .then(() => {
        (DashboardConfigurationStore as any)[type] = (
          defaultDashboardConfState as any
        )[type];
        modalStore.modal.open = false;
        modalStore.modal.body = <></>;
      })
      .finally(() => {
        setIsSubmitLoading(false);
      });
  };

  return (
    <div className="dashboardConfigSettings">
      <h3>{name || 'Settings'}</h3>
      <hr />
      <Form className="dashboardConfigWrapper">
        <div>
          <Form.Field
            label={'Select Type'}
            control={Select}
            placeholder={'Select Type'}
            clearable
            fluid
            search
            selection
            options={typeOptions}
            onKeyDown={(e: { keyCode: string; stopPropagation: any }) => {
              if (e.keyCode === 'Backspace' || e.keyCode === 'Delete')
                e.stopPropagation();
            }}
            style={{ marginTop: '5px' }}
            value={type}
            onChange={(_e: any, data: any) => setType(data.value)}
          />
          <div>
            <label>Set Width</label>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                marginTop: '10px',
              }}
            >
              <input
                type="range"
                min="0"
                max="100"
                value={flexBasis}
                onChange={(e) => setFlexBasis(e.target.value)}
              />
              <span>{flexBasis !== '0' && flexBasis}</span>
            </div>
          </div>
        </div>
        {!(
          type === 'form' ||
          type === 'workflow' ||
          type === 'tableWithForm'
        ) && (
          <div className="dashboardConfigIconWrapper">
            <Form.Field
              label={'Select Icon'}
              control={Select}
              placeholder={'Select Icon'}
              clearable
              fluid
              search
              selection
              options={iconDetails}
              onKeyDown={(e: { keyCode: string; stopPropagation: any }) => {
                if (e.keyCode === 'Backspace' || e.keyCode === 'Delete')
                  e.stopPropagation();
              }}
              style={{ marginTop: '5px' }}
              value={icon || ''}
              onChange={(_e: any, { value }: { value: string }) => {
                setIcon(value);
              }}
            />
          </div>
        )}
        {componentRenderBasedOnType()}
      </Form>
      <div className="dashboard-config-footer">
        <Button disabled={isSubmitLoading} secondary onClick={cancelHandler}>
          Cancel
        </Button>
        <Button
          disabled={isSubmitLoading}
          loading={isSubmitLoading}
          primary
          floated="right"
          onClick={saveHandler}
        >
          Save
        </Button>
      </div>
    </div>
  );
}

export default observer(Settings);
